import { useRef } from 'react';

interface PinBoxInputProps {
  length?: number;
  value: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  autoFocus?: boolean;
}

/** Entrada de PIN em caixas separadas (uma por dígito) — evita de vez o
 * problema de letter-spacing/overflow que um único input largo tinha. */
export function PinBoxInput({ length = 4, value, onChange, onEnter, autoFocus }: PinBoxInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  function setDigitAt(i: number, raw: string) {
    const clean = raw.replace(/\D/g, '').slice(-1);
    const next = digits.slice();
    next[i] = clean;
    onChange(next.join(''));
    if (clean && i < length - 1) refs.current[i + 1]?.focus();
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'Enter') {
      onEnter?.();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    onChange(text.slice(0, length));
    const focusIndex = Math.min(text.length, length) - 1;
    if (focusIndex >= 0) refs.current[focusIndex]?.focus();
  }

  return (
    <div className="pin-box-row">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="pin-box"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => setDigitAt(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}
